/**
 * Geometry Wars–style bomb geode.
 * Neon hollow capsule you fly through to detonate a radial blast.
 */
export class GeoBomb {
    constructor(x, y, orientation = null) {
        this.reset(x, y, orientation);
    }

    reset(x, y, orientation = null) {
        this.x = x;
        this.y = y;
        // Vertical bar (like the reference) or horizontal
        this.vertical = orientation === null ? Math.random() > 0.35 : !!orientation;
        this.w = this.vertical ? 18 : 64;
        this.h = this.vertical ? 64 : 18;
        this.color = '#00e8ff';
        this.phase = Math.random() * Math.PI * 2;
        this.alive = true;
        this.bob = Math.random() * Math.PI * 2;
        // Pickup radius (generous so flying over feels good)
        this.pickupR = Math.max(this.w, this.h) * 0.55 + 14;
        // Blast kills enemies within this range
        this.blastRadius = 280;
        this.lifetime = 60 * 45; // ~45s then despawn
        this.age = 0;
    }

    update() {
        this.age++;
        this.phase += 0.08;
        this.bob += 0.05;
        if (this.age >= this.lifetime) {
            this.alive = false;
            return false;
        }
        return true;
    }

    /**
     * True when player flies over / through the geode.
     */
    checkPlayerCollision(player) {
        if (!this.alive || !player) return false;
        const dx = player.x - this.x;
        const dy = player.y - this.y;
        // Slight bob does not affect collision center much
        const dist = Math.hypot(dx, dy);
        return dist < this.pickupR + player.size * 0.45;
    }

    draw(ctx) {
        if (!this.alive) return;

        const pulse = Math.sin(this.phase) * 0.5 + 0.5;
        const bobY = Math.sin(this.bob) * 2.5;
        const x = this.x;
        const y = this.y + bobY;
        const w = this.w;
        const h = this.h;
        const r = Math.min(w, h) * 0.35; // corner radius

        // Urgency flicker near expiry
        const lifeT = this.age / this.lifetime;
        let alpha = 1;
        if (lifeT > 0.75) {
            alpha = 0.45 + Math.sin(this.phase * 4) * 0.35;
        }

        ctx.save();
        ctx.globalAlpha = alpha;

        // Outer bloom
        const bloomR = Math.max(w, h) * 0.9 + 18 + pulse * 6;
        const bloom = ctx.createRadialGradient(x, y, 0, x, y, bloomR);
        bloom.addColorStop(0, `rgba(0, 232, 255, ${0.28 + pulse * 0.12})`);
        bloom.addColorStop(0.55, 'rgba(0, 180, 255, 0.08)');
        bloom.addColorStop(1, 'transparent');
        ctx.fillStyle = bloom;
        ctx.beginPath();
        ctx.arc(x, y, bloomR, 0, Math.PI * 2);
        ctx.fill();

        // Soft fill inside capsule
        ctx.shadowBlur = 18 + pulse * 10;
        ctx.shadowColor = this.color;
        ctx.fillStyle = `rgba(0, 40, 60, ${0.35 + pulse * 0.15})`;
        this._roundRect(ctx, x - w / 2, y - h / 2, w, h, r);
        ctx.fill();

        // Bright neon outline (Geometry Wars wireframe)
        ctx.lineWidth = 3;
        ctx.strokeStyle = this.color;
        ctx.shadowBlur = 22 + pulse * 12;
        ctx.shadowColor = this.color;
        this._roundRect(ctx, x - w / 2, y - h / 2, w, h, r);
        ctx.stroke();

        // Inner thin outline
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = `rgba(180, 255, 255, ${0.7 + pulse * 0.3})`;
        ctx.shadowBlur = 8;
        this._roundRect(ctx, x - w / 2 + 3, y - h / 2 + 3, w - 6, h - 6, Math.max(2, r - 2));
        ctx.stroke();

        // Center energy core
        const coreH = this.vertical ? h * 0.35 : h * 0.4;
        const coreW = this.vertical ? w * 0.35 : w * 0.35;
        ctx.shadowBlur = 14;
        ctx.fillStyle = `rgba(200, 255, 255, ${0.55 + pulse * 0.35})`;
        this._roundRect(ctx, x - coreW / 2, y - coreH / 2, coreW, coreH, 3);
        ctx.fill();

        // Orbit ticks (GW flair)
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = alpha * (0.35 + pulse * 0.4);
        ctx.shadowBlur = 6;
        const tickR = Math.max(w, h) * 0.72;
        for (let i = 0; i < 4; i++) {
            const a = this.phase * 0.6 + (i / 4) * Math.PI * 2;
            ctx.beginPath();
            ctx.arc(x, y, tickR, a, a + 0.35);
            ctx.stroke();
        }

        ctx.restore();
    }

    _roundRect(ctx, x, y, w, h, r) {
        const rr = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + rr, y);
        ctx.arcTo(x + w, y, x + w, y + h, rr);
        ctx.arcTo(x + w, y + h, x, y + h, rr);
        ctx.arcTo(x, y + h, x, y, rr);
        ctx.arcTo(x, y, x + w, y, rr);
        ctx.closePath();
    }
}
